import { useRef, useState } from 'react'
import {
  ModalDialog,
  Mtds,
  Team,
  TrashIcon,
  toUserMessage,
  XIcon
} from 'prove-common'
import { useTeamMutation } from './useTeamMutation'

export type TeamDeleteModalProps = {
  team: Team
  onClose: () => void
}

export const TeamDeleteModal = ({ team, onClose }: TeamDeleteModalProps) => {
  const deleteTeam = useTeamMutation('delete')
  const [deleteError, setDeleteError] = useState('')
  const deletePendingRef = useRef(false)
  const deleteErrorRef = useRef<HTMLHeadingElement>(null)

  const closeDelete = () => {
    if (deletePendingRef.current) return
    setDeleteError('')
    onClose()
  }

  return (
    <ModalDialog
      id='delete-team-modal'
      closedby={deleteTeam.isPending ? 'none' : 'any'}
      open
      onClose={closeDelete}
      onCancel={(event) => {
        event.preventDefault()
        closeDelete()
      }}
      aria-labelledby='delete-team-title'
    >
      <Mtds.Button
        command='request-close'
        commandfor='delete-team-modal'
        onClick={closeDelete}
        disabled={deleteTeam.isPending}
        aria-label='Lukk'
      />
      <Mtds.Prose>
        <Mtds.Heading as='h2' id='delete-team-title'>
          Slett teamet «{team.name}»?
        </Mtds.Heading>
        <p>
          Teamet «{team.name}» vil bli permanent slettet fra
          organisasjonsenheten. Dette kan ikke angres. Team som er i bruk i
          oppgavefordeling kan ikke slettes; fordelingen må endres først.
        </p>
        {deleteError && (
          <Mtds.Alert data-color='danger' role='alert'>
            <Mtds.Heading
              as='h3'
              data-size='sm'
              ref={deleteErrorRef}
              tabIndex={-1}
            >
              Teamet ble ikke slettet
            </Mtds.Heading>
            <p>{deleteError}</p>
          </Mtds.Alert>
        )}
        <Mtds.Flex data-gap='4'>
          <Mtds.Button
            disabled={deleteTeam.isPending}
            onClick={closeDelete}
            autoFocus
          >
            <XIcon />
            Avbryt
          </Mtds.Button>
          <Mtds.Button
            data-variant='primary'
            data-color='danger'
            disabled={deleteTeam.isPending}
            aria-busy={deleteTeam.isPending}
            onClick={async () => {
              if (deletePendingRef.current) return
              deletePendingRef.current = true
              setDeleteError('')
              try {
                await deleteTeam.mutateAsync(team)
                Mtds.toast.success('Teamet er slettet')
                onClose()
              } catch (error) {
                setDeleteError(
                  `${toUserMessage(error, 'slette teamet')} Kontroller om teamet er i bruk, og prøv igjen.`
                )
                setTimeout(() => deleteErrorRef.current?.focus())
              } finally {
                deletePendingRef.current = false
              }
            }}
          >
            <TrashIcon />
            {deleteTeam.isPending ? 'Sletter…' : 'Slett team'}
          </Mtds.Button>
        </Mtds.Flex>
      </Mtds.Prose>
    </ModalDialog>
  )
}
